// Kırılım bölümü — formül penceresi (türetilmiş toplam) ve hücre editörü (ham üst / üye) ortak
// (spec §6.2, §18 DR4; Editor.dc.html dört kart + Main.dc.html hover kartı). Hesap satırı
// "Kırılımdan hesaplanıyor (cinsiyet): Kadın 222 + Erkek 1.013 = 1.235", doğrulama satırları (✓ soluk;
// ✗ ve seçili kırılımlar amber), dört durum (başlık + bilgi + eylem) — HİÇBİRİ kırmızı değil (amber
// = `--color-warning-text`, asla `--color-warning`/`--color-error`). Bekleme: `aria-busy` + opacity
// 0.6 (üye kaydı sonrası grid yenilenene kadar). Portal: yalnız tek genel "alt kalem" kopyası —
// grup/boyut adı, doğrulama, set eylemi YOK. Metinler `KPI_COPY.breakdown`'dan.

import { type BreakdownEditorInfo } from "../../lib/kpi-breakdown-editor";
import { KPI_BREAKDOWN_COPY as C } from "../../lib/kpi-breakdown-copy";

export interface KpiCellBreakdownProps {
  info: BreakdownEditorInfo;
  /** toplam yeniden hesaplanıyor (hesap grubu üyesinin kaydı sürüyor) */
  recomputing?: boolean | undefined;
  /** (2) [Hücreye git: Erkek 2025] */
  onJumpTo?: ((indicatorId: string) => void) | undefined;
  /** (1) [Seti düzenle] — danışman */
  onEditSet?: (() => void) | undefined;
  /** (3) [Kırılımı bu sete ekle] — danışman (Task 14 modalı) */
  onAddBreakdownToSet?: (() => void) | undefined;
  /** "Raporlanan değer ekle" — türetilmiş toplam, danışman */
  onAddReported?: (() => void) | undefined;
}

const btn: React.CSSProperties = {
  alignSelf: "flex-start",
  background: "var(--color-surface)",
  border: "1px solid var(--color-primary)",
  color: "var(--color-primary)",
  borderRadius: 6,
  padding: "4px 10px",
  fontWeight: 600,
  fontSize: "0.8rem",
  minHeight: 32,
};

const muted = { color: "var(--color-text-3)" } as const;
const amber = { color: "var(--color-warning-text)" } as const;

export function KpiCellBreakdownSection(
  props: KpiCellBreakdownProps & { client: boolean; columnLabel: string },
): JSX.Element | null {
  const { info, client, columnLabel, recomputing } = props;
  const s = info.state;
  const body = client ? portalBody(info) : consultantBody(props);
  const reportedBtn =
    !client && info.role === "derived_total" && !info.reported && props.onAddReported ? (
      <button
        type="button"
        data-testid="kpi-breakdown-add-reported"
        style={btn}
        onClick={props.onAddReported}
      >
        {C.states.reported_fallback.action}
      </button>
    ) : null;
  if (!body && !reportedBtn && !recomputing) return null;
  return (
    <div
      data-testid="kpi-breakdown-section"
      data-state={s?.state ?? "none"}
      aria-busy={recomputing ? true : undefined}
      className="text-xs mt-2 flex flex-col gap-1"
      style={{
        color: "var(--color-text-2)",
        opacity: recomputing ? 0.6 : undefined,
        borderTop: "1px solid var(--color-border)",
        paddingTop: 8,
      }}
    >
      {recomputing ? (
        <div role="status" data-testid="kpi-breakdown-recomputing" style={muted}>
          {client ? C.portal.recomputing : C.recomputing}
        </div>
      ) : null}
      {body}
      {reportedBtn}
    </div>
  );

  function consultantBody(p: typeof props): JSX.Element | null {
    const lines: JSX.Element[] = [];
    if (info.calc)
      lines.push(
        <div key="calc" data-testid="kpi-breakdown-calc" style={{ color: "var(--color-text)" }}>
          {info.calc.line}
        </div>,
      );
    info.checks.forEach((c, i) =>
      lines.push(
        <div
          key={`c${i.toString()}`}
          data-testid="kpi-breakdown-check"
          data-kind={c.kind}
          style={c.kind === "mismatch" || c.kind === "partial" ? amber : muted}
        >
          {c.text}
        </div>,
      ),
    );
    if (info.memberLine)
      lines.push(
        <div key="member" data-testid="kpi-breakdown-member" style={muted}>
          {info.memberLine}
        </div>,
      );
    const st = stateBlock(p);
    if (st) lines.push(st);
    return lines.length > 0 ? <>{lines}</> : null;
  }

  function stateBlock(p: typeof props): JSX.Element | null {
    if (!s) return null;
    const head = (title: string, warn = false) => (
      <div
        style={{
          ...(warn ? amber : muted),
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.7rem",
          marginTop: 4,
        }}
      >
        {title}
      </div>
    );
    const action = (label: string, onClick: (() => void) | undefined, testId: string) =>
      onClick ? (
        <button type="button" data-testid={testId} style={btn} onClick={onClick}>
          {label}
        </button>
      ) : null;
    switch (s.state) {
      case "member_missing_in_set": {
        const c = C.states.member_missing_in_set;
        return (
          <div key="state" className="flex flex-col gap-1">
            {head(c.title)}
            <div style={muted}>{s.total !== null ? c.cell(s.inSet, s.total) : c.cellGeneric}</div>
            {s.missingLabels.length > 0 ? (
              <div style={muted} data-testid="kpi-breakdown-missing-members">
                {s.missingLabels.map((l) => C.tree.missingMember(l)).join(", ")}
              </div>
            ) : null}
            <div style={muted}>{c.info}</div>
            {action(c.action, p.onEditSet, "kpi-breakdown-edit-set")}
          </div>
        );
      }
      case "member_value_missing": {
        const c = C.states.member_value_missing;
        const target = info.jumpTo;
        return (
          <div key="state" className="flex flex-col gap-1">
            {head(c.title)}
            <div style={muted}>{c.cell(s.entered, s.total)}</div>
            <div style={muted}>{c.info}</div>
            {target && p.onJumpTo
              ? action(
                  c.action(target.label, columnLabel),
                  () => p.onJumpTo?.(target.indicatorId),
                  "kpi-breakdown-jump",
                )
              : null}
          </div>
        );
      }
      case "calc_group_other_set": {
        const c = C.states.calc_group_other_set;
        return (
          <div key="state" className="flex flex-col gap-1">
            {head(c.title, true)}
            <div style={amber}>{c.info}</div>
            <div style={muted}>{c.infoReported}</div>
            {action(c.action, p.onAddBreakdownToSet, "kpi-breakdown-add-to-set")}
          </div>
        );
      }
      case "reported_fallback": {
        const c = C.states.reported_fallback;
        return (
          <div key="state" className="flex flex-col gap-1">
            {head(c.title)}
            <div style={muted}>{c.badgeTitle}</div>
            <div style={muted}>{c.info}</div>
          </div>
        );
      }
    }
  }
}

/** Portal: tek genel kopya (DR4 Portal sütunu); değer bekleyen üye sayımı soluk satırda. */
function portalBody(info: BreakdownEditorInfo): JSX.Element | null {
  const s = info.state;
  if (!s && info.role !== "derived_total") return null;
  const P = C.portal;
  return (
    <>
      {s?.state === "member_value_missing" ? (
        <div style={muted}>{P.states.member_value_missing(s.entered, s.total)}</div>
      ) : null}
      <div style={muted}>
        {s?.state === "member_value_missing" ? P.waitMembers : P.autoComputed}
      </div>
    </>
  );
}
